import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from './PortalLayout';
import { useAuth } from '../../context/AuthContext';
import { Loader2, Upload, ChevronLeft } from 'lucide-react';
import { MobilePhotoUploadButton } from '../../components/MobilePhotoCropModal';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || '';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || '';

const DESTINATION_CITIES = [
  'Mexico City',
  'Tulum',
  'Playa del Carmen',
  'Mérida',
  'Puerto Vallarta',
  'San Miguel de Allende',
];

const loadCloudinaryScript = () =>
  new Promise((resolve) => {
    if (window.cloudinary) return resolve(true);
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/latest/global/all.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });

const Toggle = ({ label, checked, onChange, testId }) => (
  <label className="flex items-center justify-between py-3 cursor-pointer">
    <span className="text-[#0A1F2E] text-sm">{label}</span>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-[#2CA8A0]' : 'bg-[#EFE6CF]'
      }`}
      data-testid={testId}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transform transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </label>
);

const inputCls =
  'w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition';
const labelCls =
  'font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block';

const PortalProfileEdit = () => {
  const { user: authUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [cloudinaryReady, setCloudinaryReady] = useState(false);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    bio: '',
    current_city: '',
    destination_city: '',
    from_city: '',
    from_country: '',
    profile_photo_url: '',
    privacy_settings: {
      show_current_city: true,
      show_destination_city: true,
      show_progress: true,
      show_posts: true,
      searchable: true,
      allow_direct_messages: true,
    },
  });

  // iter 181 - account credentials block (email + password change).
  const [account, setAccount] = useState({
    current_email: '',
    pending_email_change: '',
    new_email: '',
    email_msg: '',
    email_err: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
    pwd_msg: '',
    pwd_err: '',
    savingEmail: false,
    savingPwd: false,
  });

  useEffect(() => {
    loadCloudinaryScript().then(setCloudinaryReady);
  }, []);

  useEffect(() => {
    const fetchMe = async () => {
      if (!authUser?.id) return;
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/members/${authUser.id}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Could not load profile');
        const data = await res.json();
        setForm((prev) => ({
          ...prev,
          first_name: data.first_name || '',
          bio: data.bio || '',
          current_city: data.current_city || '',
          destination_city: data.destination_city || '',
          from_city: data.from_city || '',
          from_country: data.from_country || '',
          profile_photo_url: data.profile_photo_url || '',
          privacy_settings: {
            show_current_city: data.show_current_city,
            show_destination_city: data.show_destination_city,
            show_progress: data.show_progress,
            show_posts: data.show_posts,
            searchable: data.searchable ?? true,
            allow_direct_messages: data.allow_direct_messages ?? true,
          },
        }));

        const meRes = await fetch(`${API_URL}/api/auth/me`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        if (meRes.ok) {
          const me = await meRes.json();
          setForm((prev) => ({
            ...prev,
            first_name: me.first_name ?? prev.first_name,
            last_name: me.last_name ?? '',
            phone: me.phone ?? '',
          }));
          setAccount((prev) => ({
            ...prev,
            current_email: me.email || '',
            pending_email_change: me.pending_email_change || '',
          }));
        }
      } catch (err) {
        setError(typeof err?.message === 'string' ? err.message : 'Could not load profile');
      } finally {
        setLoading(false);
      }
    };
    fetchMe();
  }, [authUser?.id]);

  const updatePrivacy = (key, value) =>
    setForm((p) => ({
      ...p,
      privacy_settings: { ...p.privacy_settings, [key]: value },
    }));

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: form.first_name,
          last_name: form.last_name,
          phone: form.phone,
          bio: form.bio,
          current_city: form.current_city,
          destination_city: form.destination_city || null,
          from_city: form.from_city,
          from_country: form.from_country,
          profile_photo_url: form.profile_photo_url,
          privacy_settings: form.privacy_settings,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        // FastAPI returns `detail` as:
        //   • a string  (custom HTTPException)
        //   • an object {error, message} (our typed errors)
        //   • an array  [{loc, msg, type, ...}] (pydantic 422)
        // Coerce to a readable string so the UI never renders [object Object].
        const detail = d?.detail;
        let msg = 'Could not save profile';
        if (typeof detail === 'string') {
          msg = detail;
        } else if (Array.isArray(detail) && detail.length) {
          msg = detail.map((x) => x?.msg || x?.message || '').filter(Boolean).join('; ') || msg;
        } else if (detail && typeof detail === 'object') {
          msg = detail.message || detail.error || msg;
        }
        throw new Error(msg);
      }
      sessionStorage.setItem('frg_profile_just_saved', 'true');
      navigate(`/portal/members/${authUser.id}`);
    } catch (err) {
      setError(typeof err?.message === 'string' ? err.message : 'Could not save profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout activeTab="profile">
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 text-[#2CA8A0] animate-spin" />
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout activeTab="profile">
      <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto" data-testid="profile-edit-page">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-[#0A1F2E]/55 hover:text-[#0A1F2E] text-sm mb-6 transition font-mono uppercase tracking-[0.12em] text-[11px]"
          data-testid="profile-edit-back"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>

        <p className="font-mono uppercase text-[10px] tracking-[0.2em] text-[#1A5C6E] mb-2">
          Profile
        </p>
        <h1 className="font-display italic text-3xl md:text-4xl text-[#0A1F2E] mb-6">
          Edit your profile
        </h1>

        {error && (
          <div className="bg-[#B91C1C]/8 border border-[#B91C1C]/30 text-[#7F1414] rounded-lg p-3 mb-4 text-sm" data-testid="profile-edit-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSave} className="space-y-6" data-testid="profile-edit-form">
          {/* Photo */}
          <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5">
            <p className={labelCls}>Profile photo</p>
            <div className="flex items-center gap-4">
              {form.profile_photo_url ? (
                <img
                  src={form.profile_photo_url}
                  alt=""
                  className="w-20 h-20 rounded-full object-cover ring-2 ring-[#EFE6CF]"
                  data-testid="current-profile-photo"
                />
              ) : (
                <div className="w-20 h-20 rounded-full bg-[#2CA8A0] text-white font-semibold flex items-center justify-center text-3xl">
                  {(form.first_name || '?')[0]?.toUpperCase() || '?'}
                </div>
              )}
              <MobilePhotoUploadButton
                cloudName={CLOUD_NAME}
                uploadPreset={UPLOAD_PRESET}
                folder="mundalo_profile_photos"
                onUploaded={(url) =>
                  setForm((p) => ({ ...p, profile_photo_url: url }))
                }
                className="inline-flex items-center gap-2 text-sm border border-[#0A1F2E]/15 bg-white text-[#0A1F2E] rounded-full px-4 py-2 hover:bg-[#F9F6EF] hover:border-[#0A1F2E]/30 transition disabled:opacity-50"
                data-testid="upload-photo-btn"
              >
                <Upload className="w-4 h-4" />
                {form.profile_photo_url ? 'Replace photo' : 'Upload photo'}
              </MobilePhotoUploadButton>
            </div>
          </section>

          {/* Name fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>First name</label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                className={inputCls}
                data-testid="edit-first-name"
              />
            </div>
            <div>
              <label className={labelCls}>Last name</label>
              <input
                type="text"
                value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                className={inputCls}
                data-testid="edit-last-name"
              />
            </div>
          </div>

          {/* iter 181 - phone number */}
          <div>
            <label className={labelCls}>Phone number</label>
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              className={inputCls}
              placeholder="+1 (555) 123-4567"
              data-testid="edit-phone"
              autoComplete="tel"
            />
          </div>

          {/* Bio */}
          <div>
            <label className={labelCls}>Bio</label>
            <textarea
              value={form.bio}
              onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value.slice(0, 300) }))}
              maxLength={300}
              rows={4}
              className={`${inputCls} resize-none`}
              placeholder="A short intro (300 characters max)"
              data-testid="edit-bio"
            />
            <div className="text-right text-xs text-[#0A1F2E]/50 mt-1" data-testid="bio-char-counter">
              {(form.bio || '').length}/300
            </div>
          </div>

          {/* Where I'm from */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Where I&rsquo;m from - City</label>
              <input
                type="text"
                value={form.from_city}
                onChange={(e) => setForm((p) => ({ ...p, from_city: e.target.value }))}
                className={inputCls}
                placeholder="e.g. Austin"
                data-testid="edit-from-city"
              />
            </div>
            <div>
              <label className={labelCls}>Where I&rsquo;m from - Country</label>
              <input
                type="text"
                value={form.from_country}
                onChange={(e) => setForm((p) => ({ ...p, from_country: e.target.value }))}
                className={inputCls}
                placeholder="e.g. United States"
                data-testid="edit-from-country"
              />
            </div>
          </div>

          {/* Current city */}
          <div>
            <label className={labelCls}>Current city</label>
            <input
              type="text"
              value={form.current_city}
              onChange={(e) => setForm((p) => ({ ...p, current_city: e.target.value }))}
              className={inputCls}
              data-testid="edit-current-city"
            />
          </div>

          {/* Destination city */}
          <div>
            <label className={labelCls}>Destination city</label>
            <select
              value={form.destination_city}
              onChange={(e) => setForm((p) => ({ ...p, destination_city: e.target.value }))}
              className={inputCls}
              data-testid="edit-destination-city"
            >
              <option value="">Select…</option>
              {DESTINATION_CITIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          {/* Privacy toggles */}
          <section className="bg-white border border-[#EFE6CF] rounded-2xl p-5 divide-y divide-[#EFE6CF]" data-testid="profile-edit-privacy">
            <div className="pb-3">
              <p className={labelCls}>Privacy</p>
              <p className="text-xs text-[#0A1F2E]/55">
                Choose what other Mundalo members can see on your profile.
              </p>
            </div>
            <Toggle
              label="Make my profile searchable"
              checked={form.privacy_settings.searchable}
              onChange={(v) => updatePrivacy('searchable', v)}
              testId="toggle-searchable"
            />
            <Toggle
              label="Allow other members to message me"
              checked={form.privacy_settings.allow_direct_messages}
              onChange={(v) => updatePrivacy('allow_direct_messages', v)}
              testId="toggle-allow-direct-messages"
            />
            <Toggle
              label="Show my current city"
              checked={form.privacy_settings.show_current_city}
              onChange={(v) => updatePrivacy('show_current_city', v)}
              testId="toggle-show-current-city"
            />
            <Toggle
              label="Show my destination city"
              checked={form.privacy_settings.show_destination_city}
              onChange={(v) => updatePrivacy('show_destination_city', v)}
              testId="toggle-show-destination-city"
            />
            <Toggle
              label="Show my lesson progress"
              checked={form.privacy_settings.show_progress}
              onChange={(v) => updatePrivacy('show_progress', v)}
              testId="toggle-show-progress"
            />
            <Toggle
              label="Show my posts"
              checked={form.privacy_settings.show_posts}
              onChange={(v) => updatePrivacy('show_posts', v)}
              testId="toggle-show-posts"
            />
          </section>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-6 py-3 rounded-full transition disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="save-profile-btn"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                'Save profile'
              )}
            </button>
          </div>
        </form>

        {/* iter 181 - Account settings (email + password change).
            Rendered OUTSIDE the profile form so an Enter keypress in the
            account fields never submits the profile form by accident. */}
        <section
          className="bg-white border border-[#EFE6CF] rounded-2xl p-5 mt-8 space-y-6"
          data-testid="account-settings"
        >
          <div>
            <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-1">Account & security</h2>
            <p className="text-xs text-[#0A1F2E]/60">
              Login email and password. Changes here take effect immediately after verification.
            </p>
          </div>

          {/* Email change */}
          <div data-testid="account-email-change">
            <p className={labelCls}>Login email</p>
            <p className="text-sm text-[#0A1F2E] mb-2" data-testid="account-current-email">
              {account.current_email || '-'}
            </p>
            {account.pending_email_change && (
              <div className="bg-[#FDF6E6] border border-[#E8A830]/40 text-[#7A4B00] rounded-lg p-3 text-xs mb-3" data-testid="account-pending-email">
                Pending verification: {account.pending_email_change}. Check that inbox for the confirmation link.
              </div>
            )}
            <label className="block text-xs text-[#0A1F2E]/60 mb-1">Change to</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                autoComplete="email"
                value={account.new_email}
                onChange={(e) => setAccount((p) => ({ ...p, new_email: e.target.value, email_msg: '', email_err: '' }))}
                className={inputCls}
                placeholder="new@example.com"
                data-testid="edit-new-email"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!account.new_email) return;
                  setAccount((p) => ({ ...p, savingEmail: true, email_msg: '', email_err: '' }));
                  try {
                    const token = localStorage.getItem('auth_token');
                    const res = await fetch(`${API_URL}/api/profile/email/request`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                      },
                      credentials: 'include',
                      body: JSON.stringify({ new_email: account.new_email }),
                    });
                    const data = await res.json().catch(() => ({}));
                    if (!res.ok) throw new Error(typeof data?.detail === 'string' ? data.detail : 'Could not request email change');
                    setAccount((p) => ({
                      ...p,
                      email_msg: data.message || 'Verification email sent.',
                      pending_email_change: account.new_email,
                      new_email: '',
                    }));
                  } catch (err) {
                    setAccount((p) => ({ ...p, email_err: err.message || 'Could not request email change' }));
                  } finally {
                    setAccount((p) => ({ ...p, savingEmail: false }));
                  }
                }}
                disabled={account.savingEmail || !account.new_email}
                className="bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-4 py-2 rounded-full transition disabled:opacity-60 inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="submit-email-change"
              >
                {account.savingEmail ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : 'Send verification'}
              </button>
            </div>
            {account.email_msg && (
              <p className="text-xs text-[#0F5132] mt-2" data-testid="email-change-msg">{account.email_msg}</p>
            )}
            {account.email_err && (
              <p className="text-xs text-[#7F1414] mt-2" data-testid="email-change-err">{account.email_err}</p>
            )}
            <p className="text-[11px] text-[#0A1F2E]/50 mt-2">
              We send a link to the new address; your current email stays active until you confirm.
            </p>
          </div>

          {/* Password change */}
          <div data-testid="account-password-change" className="border-t border-[#EFE6CF] pt-6">
            <p className={labelCls}>Password</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs text-[#0A1F2E]/60 mb-1">Current password</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={account.current_password}
                  onChange={(e) => setAccount((p) => ({ ...p, current_password: e.target.value, pwd_msg: '', pwd_err: '' }))}
                  className={inputCls}
                  data-testid="edit-current-password"
                />
              </div>
              <div>
                <label className="block text-xs text-[#0A1F2E]/60 mb-1">New password (min 8)</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={account.new_password}
                  onChange={(e) => setAccount((p) => ({ ...p, new_password: e.target.value, pwd_msg: '', pwd_err: '' }))}
                  className={inputCls}
                  data-testid="edit-new-password"
                />
              </div>
              <div>
                <label className="block text-xs text-[#0A1F2E]/60 mb-1">Confirm new password</label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={account.confirm_password}
                  onChange={(e) => setAccount((p) => ({ ...p, confirm_password: e.target.value, pwd_msg: '', pwd_err: '' }))}
                  className={inputCls}
                  data-testid="edit-confirm-password"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={async () => {
                if (!account.current_password || !account.new_password) return;
                if (account.new_password.length < 8) {
                  setAccount((p) => ({ ...p, pwd_err: 'New password must be at least 8 characters.' }));
                  return;
                }
                if (account.new_password !== account.confirm_password) {
                  setAccount((p) => ({ ...p, pwd_err: 'New password confirmation does not match.' }));
                  return;
                }
                setAccount((p) => ({ ...p, savingPwd: true, pwd_msg: '', pwd_err: '' }));
                try {
                  const token = localStorage.getItem('auth_token');
                  const res = await fetch(`${API_URL}/api/profile/password`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                      current_password: account.current_password,
                      new_password: account.new_password,
                    }),
                  });
                  const data = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error(typeof data?.detail === 'string' ? data.detail : 'Could not change password');
                  setAccount((p) => ({
                    ...p,
                    pwd_msg: 'Password updated.',
                    current_password: '',
                    new_password: '',
                    confirm_password: '',
                  }));
                } catch (err) {
                  setAccount((p) => ({ ...p, pwd_err: err.message || 'Could not change password' }));
                } finally {
                  setAccount((p) => ({ ...p, savingPwd: false }));
                }
              }}
              disabled={
                account.savingPwd || !account.current_password || !account.new_password
              }
              className="mt-3 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[11px] tracking-[0.15em] px-4 py-2 rounded-full transition disabled:opacity-60 inline-flex items-center gap-2"
              data-testid="submit-password-change"
            >
              {account.savingPwd ? (<><Loader2 className="w-4 h-4 animate-spin" /> Updating…</>) : 'Update password'}
            </button>
            {account.pwd_msg && (
              <p className="text-xs text-[#0F5132] mt-2" data-testid="pwd-change-msg">{account.pwd_msg}</p>
            )}
            {account.pwd_err && (
              <p className="text-xs text-[#7F1414] mt-2" data-testid="pwd-change-err">{account.pwd_err}</p>
            )}
          </div>
        </section>
      </div>
    </PortalLayout>
  );
};

export default PortalProfileEdit;
